import { useState } from 'react';
import { Loader2, RefreshCw, Sparkles, BookOpen, Mail, AlertTriangle } from 'lucide-react';
import {
    Dialog, DialogContent, DialogHeader, DialogTitle,
    DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import * as apiClient from '@/services/apiClient';

/**
 * ReGenerateQuestionsModal
 *
 * Lets an admin replace all questions on a SCHEDULED interview session.
 *
 * Props:
 *   open           boolean
 *   onClose        () => void
 *   session        InterviewSession  (must include _id, status, questionSetId,
 *                                     candidate.firstName, template.title)
 *   onRegenerated  (updatedSession) => void  — called on success
 */
export function ReGenerateQuestionsModal({ open, onClose, session, onRegenerated }) {
    const [mode,           setMode]           = useState('dynamic');
    const [notifyCandidate, setNotifyCandidate] = useState(true);
    const [loading,        setLoading]        = useState(false);
    const [error,          setError]          = useState('');

    // Reset local state when the modal opens
    const handleOpenChange = (isOpen) => {
        if (isOpen) { setMode('dynamic'); setNotifyCandidate(true); setError(''); }
        if (!isOpen) onClose();
    };

    const hasSet = Boolean(session?.questionSetId);

    const handleConfirm = async () => {
        if (!session) return;
        setLoading(true);
        setError('');
        try {
            const res = await apiClient.regenerateSessionQuestions(session._id, {
                mode,
                notifyCandidate,
            });
            onRegenerated?.(res.data);
            onClose();
        } catch (err) {
            setError(err.message || 'Failed to regenerate questions. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    if (!session) return null;

    const candidateName = [session.candidate?.firstName, session.candidate?.lastName]
        .filter(Boolean).join(' ') || 'the candidate';
    const roundLabel = session.roundName || (session.roundNumber ? `Round ${session.roundNumber}` : 'this session');

    return (
        <Dialog open={open} onOpenChange={handleOpenChange}>
            <DialogContent className="max-w-md">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <RefreshCw className="h-4 w-4 text-primary" />
                        Regenerate Questions
                    </DialogTitle>
                    <DialogDescription>
                        Replace all questions for <strong>{candidateName}</strong> — {roundLabel}.
                        The interview time and all other details remain unchanged.
                    </DialogDescription>
                </DialogHeader>

                <div className="space-y-4 py-1">
                    {/* Warning banner */}
                    <div className="flex items-start gap-2.5 rounded-lg border border-amber-500/30 bg-amber-500/8 px-3 py-2.5">
                        <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5 text-amber-600" />
                        <p className="text-xs text-amber-800 dark:text-amber-200 leading-snug">
                            This will <strong>replace all {session.questions?.length || ''} current questions</strong>.
                            The candidate has not started the interview yet, so this is safe.
                        </p>
                    </div>

                    {/* Mode selector */}
                    <div className="space-y-2">
                        <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                            Question source
                        </p>

                        {/* Dynamic */}
                        <button
                            type="button"
                            onClick={() => setMode('dynamic')}
                            disabled={loading}
                            className={cn(
                                'flex w-full items-start gap-3 rounded-lg border p-3 text-left transition-all',
                                mode === 'dynamic'
                                    ? 'border-primary bg-primary/5 ring-1 ring-primary/30'
                                    : 'border-border bg-background hover:border-primary/30 hover:bg-muted/30',
                            )}
                        >
                            <Sparkles className="h-4 w-4 shrink-0 mt-0.5 text-violet-500" />
                            <div>
                                <p className="text-sm font-medium">Generate fresh questions with AI</p>
                                <p className="text-xs text-muted-foreground mt-0.5 leading-snug">
                                    AI creates a new set from the job description using the same round type,
                                    difficulty, and question count.
                                </p>
                            </div>
                        </button>

                        {/* From set */}
                        <button
                            type="button"
                            onClick={() => hasSet && setMode('from_set')}
                            disabled={loading || !hasSet}
                            className={cn(
                                'flex w-full items-start gap-3 rounded-lg border p-3 text-left transition-all',
                                mode === 'from_set'
                                    ? 'border-primary bg-primary/5 ring-1 ring-primary/30'
                                    : 'border-border bg-background hover:border-primary/30 hover:bg-muted/30',
                                !hasSet && 'opacity-50 cursor-not-allowed',
                            )}
                        >
                            <BookOpen className="h-4 w-4 shrink-0 mt-0.5 text-blue-500" />
                            <div>
                                <p className="text-sm font-medium">
                                    Reload from original question set
                                    {!hasSet && (
                                        <span className="ml-2 text-[10px] text-muted-foreground font-normal">
                                            (not available — session used dynamic generation)
                                        </span>
                                    )}
                                </p>
                                <p className="text-xs text-muted-foreground mt-0.5 leading-snug">
                                    {hasSet
                                        ? 'Restores the current saved version of the original question set. Useful if the set has been updated since scheduling.'
                                        : 'This session was not created from a question set.'}
                                </p>
                            </div>
                        </button>
                    </div>

                    {/* Notify candidate */}
                    <label className="flex items-start gap-3 cursor-pointer group">
                        <input
                            type="checkbox"
                            checked={notifyCandidate}
                            onChange={e => setNotifyCandidate(e.target.checked)}
                            disabled={loading}
                            className="mt-0.5 h-4 w-4 rounded border-input accent-primary cursor-pointer"
                        />
                        <div>
                            <p className="text-sm font-medium flex items-center gap-1.5 group-hover:text-foreground">
                                <Mail className="h-3.5 w-3.5 text-muted-foreground" />
                                Notify candidate by email
                            </p>
                            <p className="text-xs text-muted-foreground mt-0.5 leading-snug">
                                Sends a brief update: "Your interview has been updated — your scheduled time
                                remains the same."
                            </p>
                        </div>
                    </label>

                    {/* Error */}
                    {error && (
                        <div className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2.5 text-sm text-destructive">
                            {error}
                        </div>
                    )}
                </div>

                <DialogFooter>
                    <Button variant="outline" onClick={onClose} disabled={loading}>
                        Cancel
                    </Button>
                    <Button onClick={handleConfirm} disabled={loading} className="gap-1.5">
                        {loading
                            ? <><Loader2 className="h-4 w-4 animate-spin" />Regenerating…</>
                            : <><RefreshCw className="h-4 w-4" />Regenerate Questions</>
                        }
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

export default ReGenerateQuestionsModal;
